// MenuContextualTraslado.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Menu, Typography, message } from "antd";
import {
  PhoneOutlined,
  EnvironmentOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  HistoryOutlined,
  SafetyCertificateOutlined,
  EditOutlined,
  ReloadOutlined,
  SwapOutlined,
  CommentOutlined,
  BarChartOutlined,
  AimOutlined,
  FileTextOutlined,
  PushpinOutlined
} from "@ant-design/icons";

import CerrarTrasladoDialog from "../Dialog/CerrarTrasladoDialog";
import ValidarTrasladoDialog from "../Dialog/ValidarTrasladoDialog";
import ObservacionesDialog from "../Dialog/ObservacionesDialog";
import HistorialTrasladoDialog from "../Dialog/HistorialTrasladoDialog";
import VerTrasladoDialog from "../Dialog/VerTrasladoDialog";
import MapDialog from "./MapDialog";
import api from "../../../axiosConfig";
import { useUser } from "../../../UserContext";
import ConfirmarTrasladoDialog from "../Dialog/ConfirmarTrasladoDialog";
import DesconfirmarTrasladoDialog from "../Dialog/DesconfirmarTrasladoDialog";
import DesvalidarTrasladoDialog from "../Dialog/DesvalidarTrasladoDialog";
import ReiniciarTrasladoDialog from "../Dialog/ReiniciarTrasladoDialog";
import CambiarEstadoTrasladoDialog from "../Dialog/CambiarEstadoTrasladoDialog";
import SeguimientoTrasladoDialog from "../Dialog/SeguimientoTrasladoDialog";
import RutaTrazadaView from "../ControlServicios/RutaTrazadaView";
import OTPVerificationDialog from "../Dialog/OTPVerificationDialog";
import CorregirCoordenadasDialog from "../Dialog/CorregirCoordenadasDialog";
import ActualizarOrigenDestinoDialog from "../Dialog/ActualizarOrigenDestinoDialog";
import FuecTrasladoDialog from "../Fuec/FuecTrasladoDialog";

// (2) Importamos (o creamos) el componente de Edición
import EditarTrasladoDialog from "../Dialog/EditarTrasladoDialog";

// Autorizaciones > Ver traslados usa este mismo menú (requerimiento 2026-10-23) con permisos propios del
// módulo Autorizaciones: a cada permiso de Centro de Control le corresponde uno de Ver traslados
const PERMISOS_AUTORIZACIONES = {
  VISUALIZAR_CC: "VISUALIZAR_TRASLADOS_A",
  REINICIAR_CC: "REINICIAR_TRASLADOS_A",
  CAMBIAR_ESTADO_CC: "CAMBIAR_ESTADO_TRASLADOS_A",
  CONFIRMACION_CC: "CONFIRMACION_TRASLADOS_A",
  VALIDACION_CC: "VALIDACION_TRASLADOS_A",
  CERRAR_CC: "CERRAR_TRASLADOS_A",
  EDITAR_CC: "EDITAR_TRASLADOS_A",
  VER_FUEC_CC: "VER_FUEC_TRASLADOS_A",
  DESCARGAR_FUEC_CC: "DESCARGAR_FUEC_TRASLADOS_A",
  CORREGIR_COORDENADAS_CC: "CORREGIR_COORDENADAS_TRASLADOS_A",
  ACTUALIZAR_ORIGEN_DESTINO_CC: "ACTUALIZAR_ORIGEN_DESTINO_A",
};

function MenuContextualTraslado({
  contextMenu,
  handleCloseContextMenu,
  handleOptionClick,
  trasladoId,
  onEstadoCambiado,
  origin,
  destination,
  traslado,
  seleccionados = [],
  onOrigenDestinoActualizado,
  modulo = "Centro de Control",
}) {
  const { user } = useUser();
  const { user: currentUser } = useUser();

  // Permisos del módulo que muestra el menú: Centro de Control, o su equivalente en Autorizaciones
  const hasPermission = useCallback(
    (permission) => {
      const codigo = modulo === "Autorizaciones" ? PERMISOS_AUTORIZACIONES[permission] : permission;
      const mismoModulo = (nombre) => nombre?.toLowerCase() === modulo.toLowerCase();
      const module = currentUser?.modulos?.find((m) => mismoModulo(m.key));
      if (!codigo || !module || module.estado !== "disponible") return false;

      return currentUser?.modulosPermisos?.some(
        (mp) => mismoModulo(mp.modulo) && mp.permiso === codigo
      );
    },
    [currentUser, modulo]
  );

  // ==========================
  // == Estados de diálogos  ==
  // ==========================
  const [cerrarDialogOpen, setCerrarDialogOpen] = useState(false);
  const [validarDialogOpen, setValidarDialogOpen] = useState(false);
  const [mapVisible, setMapVisible] = useState(false);
  const [observacionesDialogOpen, setObservacionesDialogOpen] = useState(false);
  const [historialDialogOpen, setHistorialDialogOpen] = useState(false);
  const [confirmarDialogOpen, setConfirmarDialogOpen] = useState(false);
  const [desconfirmarDialogOpen, setDesconfirmarDialogOpen] = useState(false);
  const [desvalidarDialogOpen, setDesvalidarDialogOpen] = useState(false);
  const [verDialogOpen, setVerDialogOpen] = useState(false);
  const [reiniciarDialogOpen, setReiniciarDialogOpen] = useState(false);
  const [cambiarEstadoDialogOpen, setCambiarEstadoDialogOpen] = useState(false);
  const [seguimientoDialogOpen, setSeguimientoDialogOpen] = useState(false);
  const [rutaGpsDialogOpen, setRutaGpsDialogOpen] = useState(false);
  const [validarOtpDialogOpen, setValidarOtpDialogOpen] = useState(false);
  const [fuecDialogOpen, setFuecDialogOpen] = useState(false);
  const [corregirCoordenadasOpen, setCorregirCoordenadasOpen] = useState(false);
  const [origenDestinoIds, setOrigenDestinoIds] = useState(null);
  const [cierreOtp, setCierreOtp] = useState({ estado: "Finalizado", motivo: `Validación OTP desde ${modulo}` });

  const ESTADOS_CERRADOS = useMemo(
    () => ["CANCELADO", "FALLIDO", "FINALIZADO"],
    []
  );

  // Estados pendientes de validación (pueden cerrarse pero no están cerrados)
  const ESTADOS_PENDIENTES_VALIDACION = useMemo(
    () => ["FALLIDOXVALIDAR", "FINALIZADOXVALIDAR"],
    []
  );

  const isPendienteValidacion = useMemo(
    () => ESTADOS_PENDIENTES_VALIDACION.includes(traslado.status?.toUpperCase()),
    [traslado.status, ESTADOS_PENDIENTES_VALIDACION]
  );

  // Inicializar isCerrado basado en el estado actual del traslado
  const [isCerrado, setIsCerrado] = useState(() =>
    ESTADOS_CERRADOS.includes(traslado.status?.toUpperCase())
  );

  // (3) Estado para el diálogo de edición
  const [editarDialogOpen, setEditarDialogOpen] = useState(false);

  // Determinar el estado actual del traslado
  const isConfirmed = traslado.confirmacion === "Confirmado";
  const isValidated = traslado.seguimiento === "Validado";

  // Verificar si el control de servicio está facturado o recibido (bloqueado para edición)
  const isControlBloqueado = useMemo(() => {
    // Verificar ambas posibles claves (camelCase y snake_case)
    const control = traslado.controlServicio || traslado.control_servicio;
    const estadoAuditoria = control?.estado_auditoria;
    return estadoAuditoria === 'facturado' || estadoAuditoria === 'recibido';
  }, [traslado]);

  // Firma del control retirada por novedad "firma ilegible del paciente": se cierra con OTP en su estado original
  const firmaPendienteOtp = useMemo(() => {
    const control = traslado.controlServicio || traslado.control_servicio;
    return !!control?.firma_pendiente_otp;
  }, [traslado]);

  // Obtener el label descriptivo para el estado bloqueado
  const estadoBloqueadoLabel = useMemo(() => {
    const control = traslado.controlServicio || traslado.control_servicio;
    const estadoAuditoria = control?.estado_auditoria;
    if (estadoAuditoria === 'facturado') return 'Control Facturado';
    if (estadoAuditoria === 'recibido') return 'Control Recibido';
    return '';
  }, [traslado]);

  // Efecto para actualizar el estado isCerrado cuando el traslado cambia
  useEffect(() => {
    const nuevoEstadoCerrado = ESTADOS_CERRADOS.includes(
      traslado.status?.toUpperCase()
    );
    setIsCerrado(nuevoEstadoCerrado);
  }, [traslado.status, trasladoId, ESTADOS_CERRADOS]);

  // Efecto adicional para reiniciar el estado cuando cambia el trasladoId
  useEffect(() => {
    const nuevoEstadoCerrado = ESTADOS_CERRADOS.includes(
      traslado.status?.toUpperCase()
    );
    setIsCerrado(nuevoEstadoCerrado);
  }, [trasladoId, traslado.status, ESTADOS_CERRADOS]);


  // ===============================
  // == Manejo de submenús/diálogos
  // ===============================
  const handleOpenSeguimientoDialog = () => {
    setSeguimientoDialogOpen (true);
    handleCloseContextMenu();
  };

  const handleCloseSeguimientoDialog = () => {
    setSeguimientoDialogOpen (false);
  };

  // Handler para abrir diálogo de Ruta GPS
  const handleOpenRutaGpsDialog = () => {
    setRutaGpsDialogOpen(true);
    handleCloseContextMenu();
  };

  // Handler para cerrar diálogo de Ruta GPS
  const handleCloseRutaGpsDialog = () => {
    setRutaGpsDialogOpen(false);
  };

  // Handler para abrir diálogo de Validar OTP (FinalizadoXvalidar, o FallidoXvalidar con firma pendiente)
  const handleOpenValidarOtpDialog = () => {
    setCierreOtp({
      estado: traslado.status === "FallidoXvalidar" ? "Fallido" : "Finalizado",
      motivo: `Validación OTP desde ${modulo}`,
    });
    setValidarOtpDialogOpen(true);
    handleCloseContextMenu();
  };

  // Handler para cerrar diálogo de Validar OTP
  const handleCloseValidarOtpDialog = () => {
    setValidarOtpDialogOpen(false);
  };

  // Handler cuando se verifica el OTP exitosamente
  const handleOtpVerified = (data) => {
    if (onEstadoCambiado) {
      onEstadoCambiado(trasladoId, data?.status || cierreOtp.estado, "status");
    }
    setValidarOtpDialogOpen(false);
  };

  const handleSubMenuOptionClick = (option) => {
    handleOptionClick(option);
    handleCloseContextMenu();
  };

  const handleOpenCerrarDialog = () => {
    setCerrarDialogOpen(true);
    handleCloseContextMenu();
  };

  const handleCloseCerrarDialog = () => {
    setCerrarDialogOpen(false);
  };

  const handleOpenObservaciones = () => {
    setObservacionesDialogOpen(true);
    handleCloseContextMenu();
  };

  const handleCloseObservacionesDialog = () => {
    setObservacionesDialogOpen(false);
  };

  const handleOpenHistorial = () => {
    setHistorialDialogOpen(true);
    handleCloseContextMenu();
  };

  const handleCloseHistorialDialog = () => {
    setHistorialDialogOpen(false);
  };

  const handleOpenConfirmarDialog = () => {
    setConfirmarDialogOpen(true);
    handleCloseContextMenu();
  };

  const handleCloseConfirmarDialog = () => {
    setConfirmarDialogOpen(false);
  };

  const handleOpenDesconfirmarDialog = () => {
    setDesconfirmarDialogOpen(true);
    handleCloseContextMenu();
  };

  const handleCloseDesconfirmarDialog = () => {
    setDesconfirmarDialogOpen(false);
  };

  const handleOpenDesvalidarDialog = () => {
    setDesvalidarDialogOpen(true);
    handleCloseContextMenu();
  };

  const handleCloseDesvalidarDialog = () => {
    setDesvalidarDialogOpen(false);
  };

  const handleOpenVerTrasladoDialog = () => {
    setVerDialogOpen(true);
    handleCloseContextMenu();
  };

  const handleCloseVerTrasladoDialog = () => {
    setVerDialogOpen(false);
  };

  // Abrir el diálogo de validación del traslado
  const handleOpenValidarDialog = () => {
    setValidarDialogOpen(true);
    handleCloseContextMenu();
  };

  // Cerrar el diálogo de validación del traslado
  const handleCloseValidarDialog = () => {
    setValidarDialogOpen(false);
  };

  // Handler para abrir diálogo de reinicio
  const handleOpenReiniciarDialog = () => {
    setReiniciarDialogOpen(true);
    handleCloseContextMenu();
  };

  // Handler para cerrar diálogo de reinicio
  const handleCloseReiniciarDialog = () => {
    setReiniciarDialogOpen(false);
  };

  // Handler para confirmar reinicio
  const handleReiniciarTraslado = async (trasladoId, motivo) => {
    try {
      await api.put(`/centro-control/traslados/${trasladoId}/reiniciar`, {
        usuario_id: user.id,
        motivo_reinicio: motivo,
      });

      if (onEstadoCambiado) {
        onEstadoCambiado(trasladoId, {
          status: "Programado",
          vehiculo_id: null,
          nombre_vehiculo: null,
          placa: null,
          conductor_id: null,
          nombre_conductor: null,
          seguimiento: "Sin Validar",
          confirmacion: "Sin Confirmar",
          motivo_reinicio: motivo,
        });
      }
      setIsCerrado(false);
    } catch (error) {
      // Error manejado silenciosamente
    } finally {
      setReiniciarDialogOpen(false);
    }
  };

  // == Lógica de Edición   ==
  // =========================
  const handleOpenEditarDialog = () => {
    setEditarDialogOpen(true);
    handleCloseContextMenu();
  };
  const handleCloseEditarDialog = () => setEditarDialogOpen(false);

  /**
   * Función para saber si faltan 2 horas o más para la hora de recogida.
   */
  const puedeEditar2Horas = (unTraslado) => {
    // Validar que existan los datos necesarios
    if (!unTraslado?.fecha || !unTraslado?.hora_recogida) {
      return false;
    }

    try {
      // Extraer solo la parte de la fecha (YYYY-MM-DD) sin la hora UTC
      const fechaSolo = unTraslado.fecha.split('T')[0];
      
      // Construir string de fecha y hora correctamente
      const fechaHoraString = `${fechaSolo}T${unTraslado.hora_recogida}`;
      
      // Crear objetos Date
      const fechaHoraRecogida = new Date(fechaHoraString);
      const ahora = new Date();
      
      // Verificar que la fecha sea válida
      if (isNaN(fechaHoraRecogida.getTime())) {
        return false;
      }
      
      // Calcular diferencia en milisegundos y horas
      const diffMs = fechaHoraRecogida - ahora;
      const diffHoras = diffMs / (1000 * 60 * 60);
      
      return diffHoras >= 2;
    } catch (error) {
      return false;
    }
  };

  // ==========================
  // == Llamadas a la API    ==
  // ==========================
  const handleConfirmarTraslado = async () => {
    if (!trasladoId || !user) {
      return;
    }
    try {
      await api.put(`/traslados/confirmar/${trasladoId}`, {
        usuario_id: user.id,
      });
      if (onEstadoCambiado) {
        onEstadoCambiado(trasladoId, "Confirmado", "confirmacion");
      }
    } catch (error) {
      // Error manejado silenciosamente
    }
    setConfirmarDialogOpen(false);
  };

  const handleCerrarTraslado = async (estado, mensaje, yaProcesado = false) => {
    if (!trasladoId || !user) {
      return;
    }
    try {
      if (!yaProcesado) await api.put(`/traslados/cerrar/${trasladoId}`, {
        estado,
        usuario_id: user.id,
        mensaje: mensaje || "No se proporcionó motivo",
      });

      const nuevoEstadoCerrado = ESTADOS_CERRADOS.includes(
        estado?.toUpperCase()
      );
      setIsCerrado(nuevoEstadoCerrado);

      if (onEstadoCambiado) {
        onEstadoCambiado(trasladoId, estado, "status");
      }
    } catch (error) {
      // Error manejado silenciosamente
    }
    setCerrarDialogOpen(false);
  };

  const handleDesconfirmarTraslado = async () => {
    if (!trasladoId || !user) {
      return;
    }
    try {
      await api.put(`/traslados/desconfirmar/${trasladoId}`, {
        usuario_id: user.id,
      });
      if (onEstadoCambiado) {
        onEstadoCambiado(trasladoId, "Sin Confirmar", "confirmacion");
      }
    } catch (error) {
      // Error manejado silenciosamente
    }
    setDesconfirmarDialogOpen(false);
  };

  const handleValidarTraslado = async () => {
    if (!trasladoId || !user) {
      return;
    }
    try {
      await api.put(`/traslados/validar/${trasladoId}`, {
        usuario_id: user.id,
      });
      if (onEstadoCambiado) {
        onEstadoCambiado(trasladoId, "Validado", "seguimiento");
      }
      setValidarDialogOpen(false);
    } catch (error) {
      // Error manejado silenciosamente
    }
  };

  const handleDesvalidarTraslado = async () => {
    if (!trasladoId || !user) {
      return;
    }
    try {
      await api.put(`/traslados/desvalidar/${trasladoId}`, {
        usuario_id: user.id,
      });
      if (onEstadoCambiado) {
        onEstadoCambiado(trasladoId, "Sin Validar", "seguimiento");
      }
    } catch (error) {
      // Error manejado silenciosamente
    }
    setDesvalidarDialogOpen(false);
  };

  const handleVerRuta = () => {
    if (!origin || !destination) {
      message.warning("El traslado no tiene coordenadas de origen y destino. Use \"Corregir coordenadas\" para ubicarlas.");
      handleCloseContextMenu();
      return;
    }
    setMapVisible(true);
    handleCloseContextMenu();
  };

  const handleCloseMap = () => {
    setMapVisible(false);
  };

  // Ver FUEC del traslado (R10)
  const handleOpenFuec = () => {
    setFuecDialogOpen(true);
    handleCloseContextMenu();
  };

  // Corregir coordenadas A/B (R12): cualquier estado, salvo control recibido o facturado
  const handleOpenCorregirCoordenadas = () => {
    setCorregirCoordenadasOpen(true);
    handleCloseContextMenu();
  };

  // Actualizar origen - destino: los traslados seleccionados si el del clic está entre ellos; si no, solo este
  const idsOrigenDestino =
    seleccionados.length > 0 && seleccionados.includes(trasladoId) ? seleccionados : [trasladoId];
  const handleOpenOrigenDestino = () => {
    setOrigenDestinoIds(idsOrigenDestino);
    handleCloseContextMenu();
  };

  const handleCoordenadasCorregidas = (coordenadas) => {
    if (onEstadoCambiado) {
      onEstadoCambiado(trasladoId, coordenadas.origen_coordenadas, "origen_coordenadas");
      onEstadoCambiado(trasladoId, coordenadas.destino_coordenadas, "destino_coordenadas");
    }
  };

  // Handler para abrir diálogo
  const handleOpenCambiarEstadoDialog = () => {
    setCambiarEstadoDialogOpen(true);
    handleCloseContextMenu();
  };

  // Handler para cerrar diálogo
  const handleCloseCambiarEstadoDialog = () => {
    setCambiarEstadoDialogOpen(false);
  };

  // Handler para confirmar cambio
  const handleCambiarEstado = async (nuevoEstado, motivo) => {
    if (["FinalizadoXvalidar", "FallidoXvalidar"].includes(traslado.status) && ["Finalizado", "Fallido"].includes(nuevoEstado)) {
      try {
        const { data } = await api.get('/configuraciones/otp');
        if (!data?.success) throw new Error('No se pudo consultar la configuración OTP');
        let activa = traslado.es_tai ? data.data.tai_validacion_sms_activo : data.data.otp_traslados_normales_activo;
        if (!activa) {
          // Con la firma pendiente por novedad el OTP es obligatorio aunque esté apagado en la configuración
          try {
            const control = await api.get(`/control-servicios/traslado/${trasladoId}`);
            activa = !!control.data?.data?.firma_pendiente_otp;
          } catch (error) {
            if (error.response?.status !== 404) throw error;
          }
        }
        if (activa) {
          setCierreOtp({ estado: nuevoEstado, motivo });
          setCambiarEstadoDialogOpen(false);
          setValidarOtpDialogOpen(true);
          return;
        }
      } catch (error) {
        message.error('No se pudo consultar la configuración OTP. Intente nuevamente.');
        return;
      }
    }
    try {
      await api.put(`/centro-control/traslados/${trasladoId}/cambiar-estado`, {
        nuevoEstado,
        motivo_cambio: motivo,
        usuario_id: user.id,
      });
    } catch (error) {
      // Error manejado silenciosamente
    } finally {
      setCambiarEstadoDialogOpen(false);
    }
  };

  // Función auxiliar para crear items del menú con estilo MOGOTAX
  const createMenuItem = (
    key,
    icon,
    label,
    onClick,
    disabled = false,
    danger = false
  ) => ({
    key,
    icon: (
      <div style={{ color: danger ? "#f5222d" : "#0a2e5c", fontSize: "16px" }}>
        {icon}
      </div>
    ),
    label: (
      <Typography.Text
        style={{
          color: disabled ? "#bfbfbf" : danger ? "#f5222d" : "#0a2e5c",
          fontWeight: 500,
          fontSize: "14px",
          whiteSpace: "nowrap",
          overflow: "visible",
        }}
        ellipsis={false}
      >
        {label}
      </Typography.Text>
    ),
    onClick: disabled ? undefined : onClick,
    disabled,
    style: {
      padding: "10px 12px",
      borderRadius: "4px",
      margin: "1px 0",
      minHeight: "40px",
      lineHeight: "20px",
    },
  });

  // Función auxiliar para crear submenú con estilo MOGOTAX
  const createSubMenuItem = (key, icon, label, children) => ({
    key,
    icon: <div style={{ color: "#0a2e5c", fontSize: "16px" }}>{icon}</div>,
    label: (
      <Typography.Text
        style={{ color: "#0a2e5c", fontWeight: 500, fontSize: "14px" }}
      >
        {label}
      </Typography.Text>
    ),
    children: children.map((child) => ({
      ...child,
      style: {
        padding: "10px 12px",
        borderRadius: "4px",
        margin: "1px 0",
        minHeight: "38px",
        lineHeight: "18px",
      },
    })),
    style: {
      padding: "10px 12px",
      borderRadius: "4px",
      margin: "1px 0",
      minHeight: "40px",
      lineHeight: "20px",
    },
  });

  // Crear items del menú principal
  const menuItems = [
    // 1) Ver Traslado
    hasPermission("VISUALIZAR_CC") &&
      createMenuItem(
        "ver-traslado",
        <EyeOutlined />,
        "Ver Traslado",
        handleOpenVerTrasladoDialog
      ),
    
    hasPermission("VISUALIZAR_CC") &&
    createMenuItem(
      "seguimiento",
      <BarChartOutlined/>,
      "Seguimiento",
      handleOpenSeguimientoDialog
    ),

    // 2) Reiniciar Traslado
    hasPermission("REINICIAR_CC") &&
      createMenuItem(
        "reiniciar-traslado",
        <ReloadOutlined />,
        isControlBloqueado ? `Reiniciar Traslado (${estadoBloqueadoLabel})` : "Reiniciar Traslado",
        handleOpenReiniciarDialog,
        !isCerrado || isControlBloqueado
      ),

    // 3) Cambiar Estado
    hasPermission("CAMBIAR_ESTADO_CC") &&
      isCerrado &&
      (() => {
        // Verificar si el traslado está cancelado
        const isCancelado = traslado.status?.toUpperCase() === 'CANCELADO';

        // Verificar si tiene móvil asignado (vehiculo_id o conductor_id)
        const tieneMovil = traslado.vehiculo_id || traslado.conductor_id;

        // Determinar si debe estar deshabilitado:
        // - Si el control está bloqueado (facturado/recibido) → deshabilitado
        // - Si está cancelado Y no tiene móvil → deshabilitado
        // - Si está cancelado Y tiene móvil → habilitado (para poder cambiar estado)
        const shouldDisable = isControlBloqueado || (isCancelado && !tieneMovil);

        // Determinar el label apropiado
        let label = "Cambiar Estado";
        if (isControlBloqueado) {
          label = `Cambiar Estado (${estadoBloqueadoLabel})`;
        } else if (isCancelado && !tieneMovil) {
          label = "Cambiar Estado (Sin Móvil)";
        }

        return createMenuItem(
          "cambiar-estado",
          <SwapOutlined />,
          label,
          handleOpenCambiarEstadoDialog,
          shouldDisable
        );
      })(),

    // 3.1) Validar con OTP (FinalizadoXvalidar; FallidoXvalidar solo si la firma se retiró por novedad)
    hasPermission("CAMBIAR_ESTADO_CC") &&
      (traslado.status === "FinalizadoXvalidar" ||
        (traslado.status === "FallidoXvalidar" && firmaPendienteOtp)) &&
      createMenuItem(
        "validar-otp",
        <SafetyCertificateOutlined style={{ color: "#722ED1" }} />,
        traslado.status === "FallidoXvalidar" ? "Validar con OTP (Fallido)" : "Validar con OTP",
        handleOpenValidarOtpDialog
      ),

    // 4) Submenú Teléfonos (dinámico con paciente y responsables)
    hasPermission("VISUALIZAR_CC") &&
      (() => {
        const phoneItems = [];

        // Teléfono del paciente
        if (traslado?.numero_contacto_paciente) {
          phoneItems.push(
            createMenuItem(
              "tel-paciente",
              <PhoneOutlined />,
              `Paciente: ${traslado.numero_contacto_paciente}`,
              () => handleSubMenuOptionClick("copyPhone")
            )
          );
        }

        // Teléfonos de responsables/acompañantes
        const responsables = traslado?.paciente?.responsables || [];
        responsables.forEach((resp, index) => {
          if (resp.telefono) {
            const label = resp.parentesco
              ? `${resp.parentesco}: ${resp.telefono}`
              : resp.telefono;
            phoneItems.push(
              createMenuItem(
                `tel-responsable-${index}`,
                <PhoneOutlined />,
                label,
                () => handleSubMenuOptionClick(`copyResponsiblePhone:${resp.telefono}`)
              )
            );
          }
        });

        // Si no hay responsables en el array, mostrar el campo legacy
        if (responsables.length === 0 && traslado?.numero_contacto_responsable) {
          phoneItems.push(
            createMenuItem(
              "tel-responsable",
              <PhoneOutlined />,
              traslado.numero_contacto_responsable,
              () => handleSubMenuOptionClick("copyResponsiblePhone")
            )
          );
        }

        // Si no hay teléfonos disponibles
        if (phoneItems.length === 0) {
          phoneItems.push(
            createMenuItem(
              "tel-none",
              <PhoneOutlined />,
              "Sin teléfonos disponibles",
              () => {},
              true // disabled
            )
          );
        }

        return createSubMenuItem("telefonos", <PhoneOutlined />, "Copiar teléfonos", phoneItems);
      })(),

    // 5) Confirmar/Desconfirmar
    hasPermission("CONFIRMACION_CC") &&
      (isConfirmed
        ? createMenuItem(
            "desconfirmar",
            <CloseOutlined />,
            "Desconfirmar traslado",
            handleOpenDesconfirmarDialog
          )
        : createMenuItem(
            "confirmar",
            <CheckOutlined />,
            "Confirmar traslado",
            handleOpenConfirmarDialog
          )),

    // 6) Ver ruta
    hasPermission("VISUALIZAR_CC") &&
      createMenuItem(
        "ver-ruta",
        <EnvironmentOutlined />,
        "Ver ruta",
        handleVerRuta
      ),

    // 7) Ver Ruta GPS
    hasPermission("VISUALIZAR_CC") &&
      createMenuItem(
        "ver-ruta-gps",
        <AimOutlined />,
        "Ver Ruta GPS",
        handleOpenRutaGpsDialog
      ),

    // Ver FUEC del traslado
    hasPermission("VER_FUEC_CC") &&
      createMenuItem(
        "ver-fuec",
        <FileTextOutlined />,
        traslado.fuec_id ? "Ver FUEC" : "Ver FUEC (sin FUEC)",
        handleOpenFuec,
        !traslado.fuec_id
      ),

    // Corregir coordenadas de origen (A) y destino (B)
    hasPermission("CORREGIR_COORDENADAS_CC") &&
      createMenuItem(
        "corregir-coordenadas",
        <PushpinOutlined />,
        isControlBloqueado ? `Corregir coordenadas (${estadoBloqueadoLabel})` : "Corregir coordenadas",
        handleOpenCorregirCoordenadas,
        isControlBloqueado
      ),

    // Actualizar origen - destino desde la autorización (individual o con los seleccionados)
    hasPermission("ACTUALIZAR_ORIGEN_DESTINO_CC") &&
      createMenuItem(
        "actualizar-origen-destino",
        <SwapOutlined />,
        idsOrigenDestino.length > 1
          ? `Actualizar origen - destino (${idsOrigenDestino.length} seleccionados)`
          : "Actualizar origen - destino",
        handleOpenOrigenDestino
      ),

    // 8) Historial
    hasPermission("VISUALIZAR_CC") &&
      createMenuItem(
        "historial",
        <HistoryOutlined />,
        "Ver Historial",
        handleOpenHistorial
      ),

    // 8) Observaciones
    hasPermission("VISUALIZAR_CC") &&
      createMenuItem(
        "observaciones",
        <CommentOutlined />,
        "Observaciones",
        handleOpenObservaciones
      ),

    // 9) Validar/Desvalidar
    hasPermission("VALIDACION_CC") &&
      (isValidated
        ? createMenuItem(
            "desvalidar",
            <CloseOutlined />,
            "Desvalidar traslado",
            handleOpenDesvalidarDialog
          )
        : createMenuItem(
            "validar",
            <SafetyCertificateOutlined />,
            "Validar traslado",
            handleOpenValidarDialog
          )),

    // 10) Cerrar traslado (también disponible para estados Xvalidar)
    hasPermission("CERRAR_CC") &&
      createMenuItem(
        "cerrar",
        <CloseOutlined />,
        isCerrado ? "Traslado cerrado" : "Cerrar traslado",
        handleOpenCerrarDialog,
        isCerrado, // Deshabilitado solo si está realmente cerrado
        !isCerrado || isPendienteValidacion // Mostrar punto si no está cerrado o está pendiente validación
      ),

    // 11) Editar Traslado
    hasPermission("EDITAR_CC") &&
      createMenuItem(
        "editar",
        <EditOutlined />,
        "Editar Traslado",
        handleOpenEditarDialog,
        isCerrado || !puedeEditar2Horas(traslado)
      ),
  ].filter(Boolean);

  // Función para calcular la posición inteligente del menú
  const getSmartMenuPosition = (mouseX, mouseY) => {
    // Dimensiones estimadas del menú
    const menuWidth = 260; // Ancho estimado del menú
    const itemHeight = 42; // Altura estimada por item (padding incluido)
    const menuPadding = 16; // Padding del contenedor
    const menuHeight = menuItems.length * itemHeight + menuPadding;

    // Dimensiones de la ventana
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Margen de seguridad desde los bordes
    const margin = 15;

    // Calcular posición X (horizontal)
    let adjustedX = mouseX + 5; // Pequeño offset para evitar que el cursor tape el menú

    // Verificar si se sale por la derecha
    if (adjustedX + menuWidth > windowWidth - margin) {
      // Posicionar a la izquierda del cursor
      adjustedX = mouseX - menuWidth - 5;
    }

    // Verificar si se sale por la izquierda
    if (adjustedX < margin) {
      adjustedX = margin;
    }

    // Si aún se sale por la derecha después del ajuste, centrar
    if (adjustedX + menuWidth > windowWidth - margin) {
      adjustedX = Math.max(margin, (windowWidth - menuWidth) / 2);
    }

    // Calcular posición Y (vertical)
    let adjustedY = mouseY + 5; // Pequeño offset hacia abajo

    // Verificar si se sale por abajo
    if (adjustedY + menuHeight > windowHeight - margin) {
      // Posicionar arriba del cursor
      adjustedY = mouseY - menuHeight - 5;
    }

    // Verificar si se sale por arriba
    if (adjustedY < margin) {
      adjustedY = margin;
    }

    // Si aún se sale por abajo después del ajuste, centrar
    if (adjustedY + menuHeight > windowHeight - margin) {
      adjustedY = Math.max(margin, (windowHeight - menuHeight) / 2);
    }

    return {
      x: Math.round(adjustedX),
      y: Math.round(adjustedY),
      needsScroll: menuHeight > windowHeight - 2 * margin,
    };
  };

  // Calcular la posición inteligente del menú
  const smartPosition = contextMenu
    ? getSmartMenuPosition(contextMenu.mouseX, contextMenu.mouseY)
    : null;

  // Agregar estilos CSS para el menú contextual
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .mogotax-context-menu::-webkit-scrollbar {
        width: 6px;
      }
      .mogotax-context-menu::-webkit-scrollbar-track {
        background: #f5f5f5;
        border-radius: 3px;
      }
      .mogotax-context-menu::-webkit-scrollbar-thumb {
        background: #d9d9d9;
        border-radius: 3px;
      }
      .mogotax-context-menu::-webkit-scrollbar-thumb:hover {
        background: #bfbfbf;
      }
      .mogotax-context-menu .ant-menu-item:hover,
      .mogotax-context-menu .ant-menu-submenu-title:hover {
        background-color: #e6f7ff !important;
        color: #0a2e5c !important;
      }
      .mogotax-context-menu .ant-menu-item-disabled {
        color: #bfbfbf !important;
      }
      .mogotax-context-menu .ant-menu-submenu-title {
        padding: 10px 12px !important;
        margin: 1px 0 !important;
        border-radius: 4px !important;
        min-height: 40px !important;
        line-height: 20px !important;
        display: flex !important;
        align-items: center !important;
      }
      .mogotax-context-menu .ant-menu-submenu-title:after {
        content: "▶" !important;
        font-size: 10px !important;
        color: #8c8c8c !important;
        margin-left: auto !important;
        margin-right: 0 !important;
        transform: none !important;
        transition: transform 0.2s ease !important;
      }
      .mogotax-context-menu .ant-menu-submenu-open .ant-menu-submenu-title:after {
        transform: rotate(90deg) !important;
      }
      .mogotax-context-menu .ant-menu-submenu-arrow {
        display: none !important;
      }
      .mogotax-context-menu .ant-menu-submenu {
        z-index: 10001 !important;
      }
      .mogotax-context-menu .ant-menu-sub {
        z-index: 10001 !important;
        box-shadow: 0 6px 24px rgba(10, 46, 92, 0.15), 0 2px 8px rgba(0, 0, 0, 0.08) !important;
        border: 1px solid #e8e8e8 !important;
        border-radius: 8px !important;
        background-color: #ffffff !important;
        backdrop-filter: blur(10px) !important;
        min-width: 280px !important;
        max-width: 400px !important;
        padding: 8px !important;
      }
      .mogotax-context-menu .ant-menu-sub .ant-menu-title-content {
        white-space: nowrap !important;
        overflow: visible !important;
        text-overflow: unset !important;
      }
      .mogotax-context-menu .ant-menu-sub .ant-menu-item {
        padding: 10px 12px !important;
        margin: 1px 0 !important;
        border-radius: 4px !important;
        min-height: 38px !important;
        line-height: 18px !important;
      }
      .mogotax-context-menu .ant-menu-sub .ant-menu-item:hover {
        background-color: #e6f7ff !important;
        color: #0a2e5c !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // ==========================
  // == Render del componente
  // ==========================
  const allowedCloseStates = useMemo(() => {
    const status = traslado.status;
    if (status === "Programado") {
      return ["Cancelado"];
    }
    if (status === "FallidoXvalidar") {
      // FallidoXvalidar solo puede ir a Fallido o Cancelado
      return ["Finalizado", "Cancelado", "Fallido"];
    }
    if (status === "FinalizadoXvalidar") {
      // FinalizadoXvalidar puede ir a Finalizado (con OTP), Fallido o Cancelado
      return ["Finalizado", "Fallido", "Cancelado"];
    }
    return ["Finalizado", "Cancelado", "Fallido"];
  }, [traslado.status]);

  return (
    <>
      {/* === Menú Contextual Principal con Ant Design === */}
      {contextMenu && smartPosition && (
        <div
          style={{
            position: "fixed",
            top: smartPosition.y,
            left: smartPosition.x,
            zIndex: 10000,
          }}
        >
          <Menu
            style={{
              borderRadius: "8px",
              boxShadow:
                "0 6px 24px rgba(10, 46, 92, 0.15), 0 2px 8px rgba(0, 0, 0, 0.08)",
              border: "1px solid #e8e8e8",
              backgroundColor: "#ffffff",
              backdropFilter: "blur(10px)",
              minWidth: "240px",
              maxWidth: "280px",
              padding: "8px",
              maxHeight: smartPosition?.needsScroll
                ? "calc(100vh - 30px)"
                : "auto",
              overflowY: smartPosition?.needsScroll ? "auto" : "visible",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
            className="mogotax-context-menu"
            mode="vertical"
            items={menuItems}
            onClick={() => handleCloseContextMenu()}
          />
        </div>
      )}

      {/* Overlay para cerrar el menú al hacer clic fuera */}
      {contextMenu && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            backgroundColor: "transparent",
          }}
          onClick={handleCloseContextMenu}
        />
      )}

      {/* === Dialogos y Modales === */}

      {/* (A) Cerrar traslado */}
      {hasPermission("CERRAR_CC") && cerrarDialogOpen && (
        <CerrarTrasladoDialog
          open={cerrarDialogOpen}
          onClose={handleCloseCerrarDialog}
          onSubmit={handleCerrarTraslado}
          allowedStates={allowedCloseStates}
          trasladoId={trasladoId}
          isTAI={traslado.es_tai || false}
          estadoActualTraslado={traslado.status}
        />
      )}

      {/* (B) Validar traslado */}
      {hasPermission("VALIDACION_CC") && validarDialogOpen && (
        <ValidarTrasladoDialog
          open={validarDialogOpen}
          onClose={handleCloseValidarDialog}
          onSubmit={handleValidarTraslado}
          trasladoId={trasladoId.toString()}
        />
      )}

      {/* (C) Observaciones */}
      {hasPermission("VISUALIZAR_CC") && observacionesDialogOpen && (
        <ObservacionesDialog
          open={observacionesDialogOpen}
          onClose={handleCloseObservacionesDialog}
          trasladoId={trasladoId.toString()}
        />
      )}

      {/* (D) Historial */}
      {hasPermission("VISUALIZAR_CC") && historialDialogOpen && (
        <HistorialTrasladoDialog
          open={historialDialogOpen}
          onClose={handleCloseHistorialDialog}
          trasladoId={trasladoId.toString()}
        />
      )}

      {/* (E) Confirmar */}
      {hasPermission("CONFIRMACION_CC") && confirmarDialogOpen && (
        <ConfirmarTrasladoDialog
          open={confirmarDialogOpen}
          onClose={handleCloseConfirmarDialog}
          onConfirm={handleConfirmarTraslado}
          trasladoId={trasladoId.toString()}
        />
      )}

      {/* (F) Desconfirmar */}
      {hasPermission("CONFIRMACION_CC") && desconfirmarDialogOpen && (
        <DesconfirmarTrasladoDialog
          open={desconfirmarDialogOpen}
          onClose={handleCloseDesconfirmarDialog}
          onDesconfirmar={handleDesconfirmarTraslado}
          trasladoId={trasladoId.toString()}
        />
      )}

      {/* (G) Desvalidar */}
      {hasPermission("VALIDACION_CC") && desvalidarDialogOpen && (
        <DesvalidarTrasladoDialog
          open={desvalidarDialogOpen}
          onClose={handleCloseDesvalidarDialog}
          onDesvalidar={handleDesvalidarTraslado}
          trasladoId={trasladoId.toString()}
        />
      )}

      {/* (H) Ver Traslado */}
      {hasPermission("VISUALIZAR_CC") && verDialogOpen && (
        <VerTrasladoDialog
          open={verDialogOpen}
          onClose={handleCloseVerTrasladoDialog}
          traslado={traslado}
        />
      )}

      {/* (I) Ver ruta en Mapa */}
      {hasPermission("VISUALIZAR_CC") &&
        origin &&
        destination &&
        mapVisible && (
          <MapDialog
            open={mapVisible}
            onClose={handleCloseMap}
            origin={origin}
            destination={destination}
            traslado={traslado}
          />
        )}

      {/* Diálogo de reinicio */}
      {hasPermission("REINICIAR_CC") && (
        <ReiniciarTrasladoDialog
          open={reiniciarDialogOpen}
          onClose={handleCloseReiniciarDialog}
          trasladoId={trasladoId.toString()}
          onConfirm={handleReiniciarTraslado}
        />
      )}

      {/* Diálogo de Cambiar Estado */}
      {hasPermission("CAMBIAR_ESTADO_CC") && cambiarEstadoDialogOpen && (
        <CambiarEstadoTrasladoDialog
          open={cambiarEstadoDialogOpen}
          onClose={handleCloseCambiarEstadoDialog}
          onConfirm={handleCambiarEstado}
          estadoActual={traslado.status}
        />
      )}

      {/* (J) Editar Traslado */}
      {hasPermission("EDITAR_CC") && editarDialogOpen && (
        <EditarTrasladoDialog
          open={editarDialogOpen}
          onClose={handleCloseEditarDialog}
          traslado={traslado}
          onUpdated={onEstadoCambiado}
        />
      )}

      {hasPermission("VISUALIZAR_CC") && seguimientoDialogOpen && (
        <SeguimientoTrasladoDialog
        open={seguimientoDialogOpen}
        onClose={handleCloseSeguimientoDialog}
        trasladoId={trasladoId.toString()}
        status={traslado.status}
        seguimientoEstado={traslado.seguimiento}
        onValidar={handleValidarTraslado}
        onCerrar={handleCerrarTraslado}
        allowedStates={allowedCloseStates}
        />
      )}

      {/* (K) Ver Ruta GPS */}
      {hasPermission("VISUALIZAR_CC") && (
        <RutaTrazadaView
          visible={rutaGpsDialogOpen}
          trasladoId={trasladoId}
          traslado={traslado}
          onClose={handleCloseRutaGpsDialog}
        />
      )}

      {/* (M) Ver FUEC del traslado */}
      {hasPermission("VER_FUEC_CC") && fuecDialogOpen && (
        <FuecTrasladoDialog
          open={fuecDialogOpen}
          fuecId={traslado.fuec_id}
          onClose={() => setFuecDialogOpen(false)}
          canDownload={hasPermission("DESCARGAR_FUEC_CC")}
        />
      )}

      {/* (N) Corregir coordenadas */}
      {hasPermission("CORREGIR_COORDENADAS_CC") && corregirCoordenadasOpen && (
        <CorregirCoordenadasDialog
          open={corregirCoordenadasOpen}
          onClose={() => setCorregirCoordenadasOpen(false)}
          traslado={traslado}
          onCorregido={handleCoordenadasCorregidas}
        />
      )}

      {hasPermission("ACTUALIZAR_ORIGEN_DESTINO_CC") && origenDestinoIds && (
        <ActualizarOrigenDestinoDialog
          open
          onClose={() => setOrigenDestinoIds(null)}
          trasladoIds={origenDestinoIds}
          modulo={modulo}
          onActualizado={onOrigenDestinoActualizado}
        />
      )}

      {/* (L) Validar con OTP (FinalizadoXvalidar) */}
      {hasPermission("CAMBIAR_ESTADO_CC") && validarOtpDialogOpen && (
        <OTPVerificationDialog
          open={validarOtpDialogOpen}
          onClose={handleCloseValidarOtpDialog}
          onVerified={handleOtpVerified}
          trasladoId={trasladoId}
          mensaje={cierreOtp.motivo}
          estadoCierre={cierreOtp.estado}
          tipo="validacion"
        />
      )}
    </>
  );
}

// Validación de las props con PropTypes
MenuContextualTraslado.propTypes = {
  contextMenu: PropTypes.shape({
    mouseX: PropTypes.number,
    mouseY: PropTypes.number,
  }),
  handleCloseContextMenu: PropTypes.func.isRequired,
  handleOptionClick: PropTypes.func.isRequired,
  trasladoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onEstadoCambiado: PropTypes.func,
  seleccionados: PropTypes.arrayOf(PropTypes.number),
  onOrigenDestinoActualizado: PropTypes.func,
  modulo: PropTypes.oneOf(["Centro de Control", "Autorizaciones"]),
  origin: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  destination: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  traslado: PropTypes.shape({
    confirmacion: PropTypes.string.isRequired,
    seguimiento: PropTypes.string.isRequired,
    // Agrega otros campos que uses en tu lógica
    fecha: PropTypes.string,
    hora_recogida: PropTypes.string,
    es_tai: PropTypes.bool,
  }).isRequired,
};

export default MenuContextualTraslado;
